import { useMemo, useState } from "react";
import { useShareGate } from "@/hooks/useShareGate";
import { ToolContainer, downloadBlob } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { NoticeBox } from "@/components/NoticeBox";
import { CalcSteps } from "@/components/calc/CalcSteps";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { exportKey } from "@/lib/exportCharge";
import type { CalcStep } from "@/lib/calc/steps";
import { randomIntegers, planRandom, MAX_RANDOM_COUNT } from "./random";
import {
  MAX_DECIMALS, drawDecimals, drawLottery, drawSteps, flipCoins, lotteryOdds, methodNote, parseDice, pickFromList, planDecimal, planList, planLottery, rollDice, seededFiller, type Filler,
} from "./randomPresets";

const TOOL_INFO_CONTENT = {
  what: `Generates random numbers in a range you set, with optional decimals, and has presets for dice (such as 2d6), coin flips, lottery tickets (pick 6 of 49) and picking or shuffling items from a list you paste.`,
  how: `Pick a mode. In Numbers, set the minimum, maximum and how many, choose decimal places (0 for whole numbers), and turn on "no duplicates" or "sort" if you need them. In Dice, type NdM such as 2d6, d20 or 3d8+2. In Lottery, set how many numbers to pick from how big a pool. In List, paste one item per line and pick some or shuffle them all. Whole numbers go up to very large ranges, and up to 10,000 results at a time. If the minimum is higher than the maximum the two are swapped for you. The numbers appear straight away; copying or downloading them takes one of your trial uses.`,
  tip: `By default the numbers come from your browser's crypto.getRandomValues, a cryptographically secure source, and every value in the range is equally likely. The optional seed switches to a repeatable generator that is NOT secure (anyone with the seed can recreate the list), so use it for reproducible classroom draws or test data, never for a raffle or a secret.`,
};

const SHOWN = 500;
type Mode = "numbers" | "dice" | "coin" | "lottery" | "list";
const MODES: [Mode, string][] = [["numbers", "Numbers"], ["dice", "Dice"], ["coin", "Coin"], ["lottery", "Lottery"], ["list", "List"]];

interface Outcome { items: string[]; headline: string; text: string; file: string; notes: string[]; steps: CalcStep[]; seeded: boolean }

export default function RandomNumberGenerator() {
  const { gateShare } = useShareGate();
  const [mode, setMode] = useState<Mode>("numbers");
  const [min, setMin] = useState("1");
  const [max, setMax] = useState("100");
  const [count, setCount] = useState("1");
  const [dp, setDp] = useState("0");
  const [unique, setUnique] = useState(false);
  const [sort, setSort] = useState(false);
  const [dice, setDice] = useState("2d6");
  const [flips, setFlips] = useState("1");
  const [pick, setPick] = useState("6");
  const [pool, setPool] = useState("49");
  const [list, setList] = useState("");
  const [listPick, setListPick] = useState("");
  const [seed, setSeed] = useState("");
  const [out, setOut] = useState<Outcome | null>(null);

  const dpNum = Number(dp);
  const numbersPlan = useMemo(() => {
    if (dpNum === 0) {
      const p = planRandom(min, max, count, unique);
      if (p.ok) return { ok: true as const, lo: p.min, hi: p.max, dp: 0, count: p.count, swapped: p.swapped, capped: p.capped, trimmed: false, size: p.max - p.min + BigInt(1) };
      // Typing a decimal with 0 decimal places: say how to get decimals instead of only refusing.
      const hint = /decimal/.test(p.error) && /\d\.\d/.test(`${min}${max}`) ? " To draw numbers with decimals, set Decimal places above 0." : "";
      return { ok: false as const, error: p.error + hint, field: p.field };
    }
    return planDecimal(min, max, count, unique, dp);
  }, [min, max, count, unique, dp, dpNum]);
  const dicePlan = useMemo(() => parseDice(dice), [dice]);
  const flipsPlan = useMemo(() => {
    const n = Number(flips.trim());
    return Number.isInteger(n) && n >= 1 && n <= MAX_RANDOM_COUNT ? { ok: true as const, n } : { ok: false as const, error: `Enter a whole number of flips from 1 to ${MAX_RANDOM_COUNT.toLocaleString("en-US")}.` };
  }, [flips]);
  const lotteryPlan = useMemo(() => planLottery(pick, pool), [pick, pool]);
  const listPlan = useMemo(() => planList(list, listPick), [list, listPick]);

  const seeded = seed.trim() !== "";
  const planFor = { numbers: numbersPlan, dice: dicePlan, coin: flipsPlan, lottery: lotteryPlan, list: listPlan }[mode];
  const fieldErr = (f: string) => (mode === "numbers" && !numbersPlan.ok && numbersPlan.field === f ? numbersPlan.error : "");

  const generate = () => {
    if (!planFor.ok) return;
    const rand: Filler | undefined = seeded ? seededFiller(seed) : undefined;
    const notes: string[] = [];
    let o: Outcome;
    if (mode === "numbers" && numbersPlan.ok) {
      const p = numbersPlan;
      const items = p.dp === 0 && !p.trimmed
        ? (() => { let v = randomIntegers({ min: p.lo, max: p.hi, count: p.count, unique }, rand); if (sort) v = [...v].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0)); return v.map(String); })()
        : drawDecimals(p, unique, sort, rand);
      if (p.swapped) notes.push("Minimum was higher than maximum, so the two were swapped.");
      if (p.trimmed) notes.push(`With ${p.dp} decimal place${p.dp === 1 ? "" : "s"} the range was trimmed to the numbers that fit: the smallest possible is rounded up and the largest rounded down, so nothing falls outside what you typed.`);
      if (p.capped) notes.push(`You asked for more than ${MAX_RANDOM_COUNT.toLocaleString("en-US")}, so ${MAX_RANDOM_COUNT.toLocaleString("en-US")} numbers were generated.`);
      o = { items, headline: `${items.length.toLocaleString("en-US")} number${items.length === 1 ? "" : "s"}`, text: items.join(", "), file: items.join("\n"), notes, steps: drawSteps(p, unique, seeded), seeded };
    } else if (mode === "dice" && dicePlan.ok) {
      const r = rollDice(dicePlan, rand);
      o = {
        items: r.rolls.map(String), headline: `Total ${r.total.toLocaleString("en-US")}`, text: r.text, file: r.text, notes: dicePlan.modifier ? [`Dice add up to ${r.sum.toLocaleString("en-US")}, then ${dicePlan.modifier > 0 ? "plus" : "minus"} ${Math.abs(dicePlan.modifier)}.`] : [],
        steps: [{ label: "Roll each die", formula: `each die is a whole number from 1 to ${dicePlan.sides}, every face equally likely`, inputs: { dice: dicePlan.dice, sides: dicePlan.sides }, result: r.rolls.slice(0, 20).join(", ") + (r.rolls.length > 20 ? ", ..." : "") }, { label: "Add up", formula: "sum of the dice + modifier", inputs: { sum: r.sum, modifier: dicePlan.modifier }, result: r.total }], seeded,
      };
    } else if (mode === "coin" && flipsPlan.ok) {
      const r = flipCoins(flipsPlan.n, rand);
      o = { items: r.flips.slice(0, SHOWN), headline: `${r.heads} heads, ${r.tails} tails`, text: flipsPlan.n === 1 ? r.flips[0] : `${r.flips.join(", ")}\nHeads ${r.heads}, Tails ${r.tails}`, file: r.flips.join("\n") + `\nHeads ${r.heads}, Tails ${r.tails}`, notes: [], steps: [], seeded };
    } else if (mode === "lottery" && lotteryPlan.ok) {
      const t = drawLottery(lotteryPlan, rand);
      o = {
        items: t.map(String), headline: `${lotteryPlan.pick} of ${lotteryPlan.of}, sorted`, text: t.join(", "), file: t.join("\n"),
        notes: [`Any one ticket of this size has a 1 in ${lotteryOdds(lotteryPlan).toLocaleString("en-US")} chance. A random pick is no more likely to win than any other ticket, and it does not predict a real draw.`],
        steps: [], seeded,
      };
    } else if (mode === "list" && listPlan.ok) {
      const items = pickFromList(listPlan, rand);
      if (new Set(listPlan.items).size < listPlan.items.length) notes.push("Some lines are the same. Each line counts as its own entry, so a repeated line has a better chance of being drawn.");
      o = { items, headline: listPlan.pick === listPlan.items.length ? `Shuffled ${items.length} item${items.length === 1 ? "" : "s"}` : `${items.length} picked from ${listPlan.items.length}`, text: items.join("\n"), file: items.join("\n"), notes, steps: [], seeded };
    } else return;
    setOut(o);
  };

  // The same numbers delivered again (copy, then download) are one use; a new draw is a new output.
  const outKey = exportKey({ items: out?.items, text: out?.text });
  const copy = () => out && gateShare(async () => {
    try { await navigator.clipboard.writeText(out.text); toast.success("Copied to clipboard"); return true; }
    catch { toast.error("Could not copy. Select the numbers and copy them by hand."); return false; }
  }, outKey);
  const download = () => out && void gateShare(async () => {
    // The gate has decided and charges after this; the file itself is therefore not charged a second time.
    downloadBlob(new Blob([out.file + "\n"], { type: "text/plain;charset=utf-8" }), "random-numbers.txt", { free: true });
    return true;
  }, outKey);

  const shown = out?.items.slice(0, SHOWN) ?? [];

  return (
    <ToolContainer>
      <div className="flex flex-wrap gap-2" role="group" aria-label="What to generate">
        {MODES.map(([id, label]) => (
          <Button key={id} type="button" size="sm" variant={mode === id ? "default" : "outline"} aria-pressed={mode === id} onClick={() => { setMode(id); setOut(null); }}>{label}</Button>
        ))}
      </div>

      {mode === "numbers" && (<>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
          {([["Min", "rng-min", min, setMin, "min"], ["Max", "rng-max", max, setMax, "max"], ["How many", "rng-count", count, setCount, "count"]] as const).map(([label, id, val, set, field]) => (
            <div key={id}>
              <Label htmlFor={id} className="mb-2 block">{label}</Label>
              <Input id={id} inputMode="decimal" value={val} onChange={e => set(e.target.value)} aria-invalid={!!fieldErr(field)} aria-describedby={fieldErr(field) ? `${id}-err` : undefined} />
              {fieldErr(field) && <p id={`${id}-err`} role="alert" className="text-xs text-destructive mt-1">{fieldErr(field)}</p>}
            </div>
          ))}
          <div>
            <Label htmlFor="rng-dp" className="mb-2 block">Decimal places</Label>
            <select id="rng-dp" value={dp} onChange={e => setDp(e.target.value)} className="w-full border rounded-md px-3 py-2 bg-background text-sm h-9" aria-invalid={!!fieldErr("dp")}>
              {Array.from({ length: MAX_DECIMALS + 1 }, (_, i) => <option key={i} value={i}>{i === 0 ? "0 (whole numbers)" : i}</option>)}
            </select>
          </div>
        </div>
        {numbersPlan.ok && numbersPlan.swapped && <p className="text-xs text-muted-foreground mt-2">Minimum is higher than maximum, so the two will be swapped before drawing.</p>}
        {numbersPlan.ok && numbersPlan.capped && <p className="text-xs text-muted-foreground mt-2">The most you can generate at once is {MAX_RANDOM_COUNT.toLocaleString("en-US")}.</p>}
        <div className="flex items-center gap-2 mt-4">
          <Switch id="rng-unique" checked={unique} onCheckedChange={setUnique} />
          <Label htmlFor="rng-unique" className="text-sm">No duplicates (unique numbers only)</Label>
        </div>
        {fieldErr("unique") && <p role="alert" className="text-xs text-destructive mt-1">{fieldErr("unique")}</p>}
        <div className="flex items-center gap-2 mt-2">
          <Switch id="rng-sort" checked={sort} onCheckedChange={setSort} />
          <Label htmlFor="rng-sort" className="text-sm">Sort the results</Label>
        </div>
      </>)}

      {mode === "dice" && (
        <div className="mt-4 max-w-sm">
          <Label htmlFor="rng-dice" className="mb-2 block">Dice (NdM, such as 2d6, d20 or 3d8+2)</Label>
          <Input id="rng-dice" value={dice} onChange={e => setDice(e.target.value)} aria-invalid={!dicePlan.ok} aria-describedby={dicePlan.ok ? undefined : "rng-dice-err"} />
          {!dicePlan.ok && <p id="rng-dice-err" role="alert" className="text-xs text-destructive mt-1">{dicePlan.error}</p>}
        </div>
      )}

      {mode === "coin" && (
        <div className="mt-4 max-w-sm">
          <Label htmlFor="rng-flips" className="mb-2 block">How many flips</Label>
          <Input id="rng-flips" inputMode="numeric" value={flips} onChange={e => setFlips(e.target.value)} aria-invalid={!flipsPlan.ok} aria-describedby={flipsPlan.ok ? undefined : "rng-flips-err"} />
          {!flipsPlan.ok && <p id="rng-flips-err" role="alert" className="text-xs text-destructive mt-1">{flipsPlan.error}</p>}
        </div>
      )}

      {mode === "lottery" && (
        <div className="mt-4">
          <div className="grid grid-cols-2 gap-4 max-w-sm">
            <div><Label htmlFor="rng-pick" className="mb-2 block">Numbers to pick</Label><Input id="rng-pick" inputMode="numeric" value={pick} onChange={e => setPick(e.target.value)} aria-invalid={!lotteryPlan.ok} /></div>
            <div><Label htmlFor="rng-pool" className="mb-2 block">Out of (1 to this)</Label><Input id="rng-pool" inputMode="numeric" value={pool} onChange={e => setPool(e.target.value)} aria-invalid={!lotteryPlan.ok} /></div>
          </div>
          {!lotteryPlan.ok && <p role="alert" className="text-xs text-destructive mt-1">{lotteryPlan.error}</p>}
          <p className="text-xs text-muted-foreground mt-2">Six from 49 is the classic ticket. The numbers come out different and sorted.</p>
        </div>
      )}

      {mode === "list" && (
        <div className="mt-4 space-y-3">
          <div>
            <Label htmlFor="rng-list" className="mb-2 block">Your list, one item per line</Label>
            <textarea id="rng-list" value={list} onChange={e => setList(e.target.value)} rows={6} className="w-full border rounded-md px-3 py-2 bg-background text-sm" placeholder={"Ana\nBen\nCy"} aria-invalid={!listPlan.ok && list.trim() !== ""} />
            <p className="text-xs text-muted-foreground mt-1">The list stays in your browser; nothing is uploaded.</p>
          </div>
          <div className="max-w-xs">
            <Label htmlFor="rng-listpick" className="mb-2 block">How many to pick (empty shuffles the whole list)</Label>
            <Input id="rng-listpick" inputMode="numeric" value={listPick} onChange={e => setListPick(e.target.value)} />
          </div>
          {!listPlan.ok && list.trim() !== "" && <p role="alert" className="text-xs text-destructive">{listPlan.error}</p>}
        </div>
      )}

      <div className="mt-4 max-w-sm">
        <Label htmlFor="rng-seed" className="mb-2 block">Seed (optional, repeatable draws)</Label>
        <Input id="rng-seed" value={seed} onChange={e => setSeed(e.target.value)} placeholder="Leave empty for secure random" />
      </div>
      {seeded && (
        <NoticeBox className="mt-2 text-sm" role="status" testId="rng-seed-warning">With a seed the same numbers come out every time, and the generator is not cryptographically secure. Do not use a seed for a raffle, a password or anything secret. Clear the seed to use the secure generator.</NoticeBox>
      )}

      <Button className="mt-4" onClick={generate} disabled={!planFor.ok}>
        {mode === "dice" ? "Roll" : mode === "coin" ? "Flip" : mode === "list" ? "Draw" : "Generate"}
      </Button>

      {out && (
        <Card className="mt-6 p-6" data-testid="rng-results">
          <p className="text-sm text-muted-foreground mb-3" data-testid="rng-headline">{out.headline}</p>
          <div className="flex flex-wrap gap-2 mb-4">
            {shown.map((n, i) => (
              <span key={i} className="inline-flex items-center justify-center min-w-[3rem] px-3 py-2 rounded-lg bg-primary/10 text-primary font-bold text-lg break-all">{n}</span>
            ))}
          </div>
          {out.items.length > SHOWN && <p className="text-xs text-muted-foreground mb-3">Showing the first {SHOWN} of {out.items.length.toLocaleString("en-US")}. Copy or download to get them all.</p>}
          {out.notes.map((n, i) => <p key={i} className="text-xs text-muted-foreground mb-2">{n}</p>)}
          <p className="text-xs text-muted-foreground mb-3" data-testid="rng-method">{methodNote(out.seeded)}</p>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={copy}>Copy Results</Button>
            <Button size="sm" variant="outline" onClick={download}>Download .txt</Button>
          </div>
          {out.steps.length > 0 && <div className="mt-4"><CalcSteps steps={out.steps} title="How this was generated" /></div>}
        </Card>
      )}
      <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
